import React from 'react';
import { useRecoilValue } from 'recoil';
import { SpecialOffersByCategory } from '../../store';
import cn from 'classnames';
import styles from './index.module.scss';
import { OfferMainCard } from '../offer-main-card';
import { OfferSecondaryCard } from '../offer-secondary-card';
import { OfferTertiaryCard } from '../offer-tertiary-card';
import { useDeviceType, DeviceType } from './offerDeviceType';

interface OffersListProps {
  secondaryTitle?: string;
}

export const OffersList = ({
  secondaryTitle,
}: OffersListProps): JSX.Element => {
  const deviceType = useDeviceType();
  const specialOffers = useRecoilValue(SpecialOffersByCategory);
  const mainTiles = specialOffers.find((o) => o.displayCategory === 'Main');
  const secondaryTiles = specialOffers
    .filter((o) => o.displayCategory === 'Secondary')
    .slice(0, 3);
  const tertiaryTiles = specialOffers.filter(
    (o) => o.displayCategory === 'Tertiary' || !o.displayCategory
  );
  const hideMainContent = secondaryTiles.length >= 2 || deviceType === 'mobile';
  const hideSecondaryContent =
    secondaryTiles.length > 1 || deviceType === 'mobile';

  const tertiaryRowCount = 3;

  return (
    <div className={styles.specialOffers}>
      <div className={styles.headerOffers}>
        <div
          className={cn(styles.mainTiles, {
            [styles.showMainContent]: !hideMainContent,
          })}
        >
          {mainTiles && (
            <OfferMainCard
              key={mainTiles.uniqueName}
              offer={mainTiles}
              hideContent={hideMainContent}
              deviceType={deviceType}
            />
          )}
        </div>
        <div
          className={`${styles.secondaryTiles} ${
            secondaryTiles.length === 3
              ? styles.threeTiles
              : secondaryTiles.length === 2
              ? styles.twoTiles
              : secondaryTiles.length === 1
              ? styles.oneTile
              : ''
          }`}
        >
          {secondaryTiles.map((offer, index) => {
            // image selection rules:
            // if mobile viewport -> always 'mobile'
            // if 3 secondary tiles on non-mobile -> first uses 'desktop', others use 'tablet'
            // if 2 secondary tiles on non-mobile -> use 'desktop'
            // if single secondary tile on non-mobile -> use 'tablet'
            const imageDeviceType: DeviceType =
              deviceType === 'mobile'
                ? 'mobile'
                : secondaryTiles.length === 3
                ? index === 0
                  ? 'desktop'
                  : 'tablet'
                : secondaryTiles.length === 2
                ? 'desktop'
                : secondaryTiles.length === 1
                ? 'tablet'
                : deviceType;

            const isOneTileLayout =
              secondaryTiles.length === 1 && deviceType !== 'mobile';
            const layoutByCount =
              secondaryTiles.length === 3
                ? 'threeTileLayout'
                : secondaryTiles.length === 2
                ? 'twoTileLayout'
                : secondaryTiles.length === 1
                ? 'oneTileLayout'
                : '';
            return (
              <OfferSecondaryCard
                key={offer.uniqueName}
                offer={offer}
                hideContent={hideSecondaryContent}
                deviceType={imageDeviceType}
                layoutClass={isOneTileLayout ? 'oneTileLayout' : layoutByCount}
              />
            );
          })}
        </div>
      </div>
      {secondaryTitle && (
        <div
          className={styles.secondaryTitle}
          dangerouslySetInnerHTML={{ __html: secondaryTitle as string }}
        />
      )}
      <div className={styles.tertiaryTiles}>
        {tertiaryTiles.map((offer, index) => (
          <OfferTertiaryCard
            key={offer.uniqueName}
            offer={offer}
            deviceType={deviceType}
            isOneItemTile={
              tertiaryTiles.length - 1 === index &&
              tertiaryTiles.slice(
                tertiaryTiles.length === 1
                  ? 0
                  : Math.ceil(tertiaryTiles.length / tertiaryRowCount) >= 3
                  ? tertiaryRowCount *
                    (Math.ceil(tertiaryTiles.length / tertiaryRowCount) - 1)
                  : tertiaryRowCount
              ).length === 1
            }
          />
        ))}
      </div>
    </div>
  );
};
